/* ═══════════════════════════════════════════════════════════════
   forecasting2/utils — date/color/capacity helpers. Duplicated (not
   imported) from the old pages/forecasting/_shared.tsx on purpose —
   that folder is deleted once this replacement is verified centrally,
   and this new surface must not depend on code scheduled for removal.
   ═══════════════════════════════════════════════════════════════ */

import { PROJECT_PALETTE } from '../../lib/palette';
import type { TeamMember } from '../../../shared/types';
import type { WeekCapacity } from './types';

export { PROJECT_PALETTE };

/** Stable hue per project — same project shows the same color across weeks. */
export function projectColor(projectId: string): string {
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = (hash * 31 + projectId.charCodeAt(i)) | 0;
  }
  return PROJECT_PALETTE[Math.abs(hash) % PROJECT_PALETTE.length];
}

export const VACATION_COLOR = 'var(--tm)';

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Returns the Monday-of-week ISO date for any input date string. Local
 *  copy of timeService's mondayOf — that file is off-limits (concurrent
 *  agent territory) and this is 6 lines of pure date math. */
export function mondayOf(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = dt.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setUTCDate(dt.getUTCDate() + diff);
  return dt.toISOString().slice(0, 10);
}

/** Whole-day delta between two yyyy-mm-dd dates. Negative if `a` > `b`. */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

export function addDays(isoDate: string, n: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

/** Monday..Sunday ISO strings for the week starting at `weekStart`. */
export function weekDays(weekStart: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < 7; i++) out.push(addDays(weekStart, i));
  return out;
}

/** `count` consecutive Monday-anchored week-start dates beginning at
 *  `firstWeekStart` (already normalized to a Monday by the caller). */
export function weekWindow(firstWeekStart: string, count: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(addDays(firstWeekStart, i * 7));
  return out;
}

export function formatShort(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getUTCMonth()]} ${dt.getUTCDate()}`;
}

export function formatLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${days[dt.getUTCDay()]}, ${formatShort(iso)}, ${y}`;
}

/** yyyy-mm-dd -> "Mon D" label for a week's start (used in the grid's
 *  column headers). */
export function weekLabel(weekStart: string): string {
  return `${formatShort(weekStart)} – ${formatShort(addDays(weekStart, 6))}`;
}

/** Split a weekly total evenly across Mon–Fri, rounded to 0.5h increments,
 *  summing back to EXACTLY `hours` (any 0.5h remainder lands on the
 *  earliest days). This is the "hours write as even Mon–Fri split
 *  server-side" default — the per-day expansion drawer is the escape
 *  hatch for uneven allocation. */
export function splitWeekEvenly(hours: number): [number, number, number, number, number] {
  if (hours <= 0) return [0, 0, 0, 0, 0];
  const units = Math.round(hours * 2); // total half-hour units
  const base = Math.floor(units / 5);
  let remainder = units - base * 5;
  const out: number[] = [];
  for (let i = 0; i < 5; i++) {
    let u = base;
    if (remainder > 0) {
      u += 1;
      remainder -= 1;
    }
    out.push(u / 2);
  }
  return out as [number, number, number, number, number];
}

/** One-caution capacity calc — kills the old four-color alarm soup.
 *  Everything below/at target reads neutral; only over-allocation gets
 *  the caution treatment (var(--color-caution)), applied by the caller. */
export function computeWeekCapacity(
  member: TeamMember,
  hoursThisWeek: number,
  vacationHoursThisWeek: number
): WeekCapacity {
  const isPartTime = member.employmentType === 'part_time_hourly';
  const rawTarget = member.weeklyCapacityHours ?? (isPartTime ? 20 : 40);
  const workedHours = hoursThisWeek - vacationHoursThisWeek;
  const availableHours = Math.max(0, rawTarget - vacationHoursThisWeek);
  const overAllocated = availableHours > 0 && workedHours > availableHours * 1.1;
  return {
    rawTarget,
    vacationHours: vacationHoursThisWeek,
    workedHours,
    availableHours,
    totalHours: hoursThisWeek,
    isPartTime,
    overAllocated,
  };
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
